import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Line } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend } from 'chart.js';
import LoaderOverlay from './LoaderOverlay';
import { showAppAlert } from '../utils/alertService';
import '../styles/summariesPanel.css';

const API_URL = process.env.REACT_APP_API_BASE_URL;

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend);

const sortableHeaderStyle = {
  background: 'transparent',
  border: 0,
  color: 'inherit',
  cursor: 'pointer',
  font: 'inherit',
  fontWeight: 'bold',
  padding: 0,
};

function getInitialDates() {
  const today = new Date();
  const firstDay = new Date(today.getFullYear(), today.getMonth(), 1).toISOString().split('T')[0];
  const lastDay = new Date(today.getFullYear(), today.getMonth() + 1, 0).toISOString().split('T')[0];
  return { firstDay, lastDay };
}

function sortRows(rows, sortConfig) {
  return [...rows].sort((a, b) => {
    const valueA = sortConfig.key === 'total_emails_enviados'
      ? Number(a.total_emails_enviados || 0)
      : sortConfig.key === 'participacao'
        ? Number(a.participacao || 0)
        : a[sortConfig.key] || '';
    const valueB = sortConfig.key === 'total_emails_enviados'
      ? Number(b.total_emails_enviados || 0)
      : sortConfig.key === 'participacao'
        ? Number(b.participacao || 0)
        : b[sortConfig.key] || '';

    if (typeof valueA === 'number' && typeof valueB === 'number') {
      return sortConfig.direction === 'asc' ? valueA - valueB : valueB - valueA;
    }

    return sortConfig.direction === 'asc'
      ? String(valueA).localeCompare(String(valueB))
      : String(valueB).localeCompare(String(valueA));
  });
}

function SummaryCompanies({ lojistas = [] }) {
  const initialDates = getInitialDates();
  const [companyId, setCompanyId] = useState('');
  const [startDate, setStartDate] = useState(initialDates.firstDay);
  const [endDate, setEndDate] = useState(initialDates.lastDay);
  const [summary, setSummary] = useState(null);
  const [ticketMedio, setTicketMedio] = useState([]);
  const [emailParticipation, setEmailParticipation] = useState([]);
  const [hasSummarySearched, setHasSummarySearched] = useState(false);
  const [hasEmailParticipationSearched, setHasEmailParticipationSearched] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState('Buscando dados...');
  const [emailParticipationSort, setEmailParticipationSort] = useState({ key: 'participacao', direction: 'desc' });
  const hasLoadedInitialEmailParticipation = useRef(false);
  const token = localStorage.getItem('token');

  const validateDates = useCallback((context) => {
    if (!startDate || !endDate) {
      showAppAlert('info', `Selecione um periodo para buscar ${context}.`);
      return false;
    }
    if (new Date(endDate) < new Date(startDate)) {
      showAppAlert('warning', 'A data de fim nao pode ser anterior a data de inicio.');
      return false;
    }
    return true;
  }, [endDate, startDate]);

  const fetchEmailParticipation = useCallback(async (showErrorAlert = true) => {
    if (!validateDates('a participação de emails')) {
      return;
    }

    setHasEmailParticipationSearched(true);
    const params = new URLSearchParams({
      dataInicio: startDate,
      dataFim: endDate,
    });
    const response = await fetch(`${API_URL}/admin/email-participation?${params.toString()}`, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });
    const data = await response.json();

    if (response.ok && Array.isArray(data)) {
      setEmailParticipation(data);
    } else {
      setEmailParticipation([]);
      if (showErrorAlert) {
        showAppAlert('error', data.message || data.error || 'Erro ao buscar participação de emails.');
      }
    }
  }, [endDate, startDate, token, validateDates]);

  useEffect(() => {
    if (hasLoadedInitialEmailParticipation.current) {
      return;
    }
    hasLoadedInitialEmailParticipation.current = true;

    const loadInitialEmailParticipation = async () => {
      setLoadingMessage('Buscando participação de emails...');
      setIsLoading(true);
      try {
        await fetchEmailParticipation(false);
      } catch (error) {
        setEmailParticipation([]);
        setHasEmailParticipationSearched(true);
      } finally {
        setIsLoading(false);
      }
    };

    loadInitialEmailParticipation();
  }, [fetchEmailParticipation]);

  const fetchSummaryCompanies = useCallback(async () => {
    if (!validateDates('o fluxo')) {
      return;
    }

    setLoadingMessage('Buscando fluxo de lojistas...');
    setIsLoading(true);
    try {
      await fetchEmailParticipation();

      if (!companyId) {
        return;
      }

      setHasSummarySearched(true);
      const params = new URLSearchParams({
        companyId,
        dataInicio: startDate,
        dataFim: endDate,
      });
      const response = await fetch(`${API_URL}/admin/summaries?${params.toString()}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      const data = await response.json();

      if (response.ok) {
        setSummary(data.summary || null);
        setTicketMedio(Array.isArray(data.ticket_medio) ? data.ticket_medio : []);
      } else {
        setSummary(null);
        setTicketMedio([]);
        showAppAlert('error', data.error || data.message || 'Erro ao buscar fluxo de lojistas.');
      }
    } catch (error) {
      setSummary(null);
      setTicketMedio([]);
      setEmailParticipation([]);
      showAppAlert('error', 'Ocorreu um erro de conexao ao buscar o fluxo de lojistas.');
    } finally {
      setIsLoading(false);
    }
  }, [companyId, endDate, fetchEmailParticipation, startDate, token, validateDates]);

  const handleCompanyChange = (value) => {
    setCompanyId(value);
    setSummary(null);
    setTicketMedio([]);
    setHasSummarySearched(false);
  };

  const handleStartDateChange = (value) => {
    setStartDate(value);
    setSummary(null);
    setTicketMedio([]);
    setEmailParticipation([]);
    setHasSummarySearched(false);
    setHasEmailParticipationSearched(false);
  };

  const handleEndDateChange = (value) => {
    setEndDate(value);
    setSummary(null);
    setTicketMedio([]);
    setEmailParticipation([]);
    setHasSummarySearched(false);
    setHasEmailParticipationSearched(false);
  };

  const handleEmailParticipationSort = (key) => {
    setEmailParticipationSort((currentSort) => ({
      key,
      direction: currentSort.key === key && currentSort.direction === 'asc' ? 'desc' : 'asc',
    }));
  };

  const getSortLabel = (label, key) => {
    if (emailParticipationSort.key !== key) return label;
    return `${label} ${emailParticipationSort.direction === 'asc' ? '▲' : '▼'}`;
  };

  const sortedEmailParticipation = sortRows(emailParticipation, emailParticipationSort);

  const ticketMedioChartData = {
    labels: ticketMedio.map((item) => new Date(item.date).toLocaleDateString('pt-BR')),
    datasets: [{
      label: 'Ticket Medio (R$)',
      data: ticketMedio.map((item) => item.average_ticket),
      fill: false,
      borderColor: 'rgb(75, 192, 192)',
      tension: 0.1,
    }],
  };

  const lineChartOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: 'top',
      },
      title: {
        display: true,
        text: 'Evolucao do Ticket Medio',
      },
    },
    maintainAspectRatio: false,
  };

  return (
    <section className="dashboard-section">
      <LoaderOverlay isLoading={isLoading} message={loadingMessage} />
      <h3>Resumo de Operacoes dos Lojistas</h3>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '20px', marginBottom: '20px', padding: '15px', backgroundColor: '#f5f5f5', borderRadius: '8px' }}>
        <div className="filter-group">
          <label htmlFor="fluxoCompanyId">Lojista:</label><br />
          <select
            id="fluxoCompanyId"
            value={companyId}
            onChange={(e) => handleCompanyChange(e.target.value)}
            className="input-style"
          >
            <option value="">Selecione um Lojista</option>
            {lojistas.map((lojista, index) => {
              const optionCompanyId = lojista.companyId || lojista.id;
              return (
                <option key={optionCompanyId || index} value={optionCompanyId || ''}>
                  {lojista.nomeFantasia || lojista.email || `Lojista ${index + 1}`}
                </option>
              );
            })}
          </select>
        </div>
        <div className="filter-group">
          <label htmlFor="fluxoStartDate">Data de Inicio:</label><br />
          <input
            type="date"
            id="fluxoStartDate"
            value={startDate}
            onChange={(e) => handleStartDateChange(e.target.value)}
            className="input-style"
          />
        </div>
        <div className="filter-group">
          <label htmlFor="fluxoEndDate">Data de Fim:</label><br />
          <input
            type="date"
            id="fluxoEndDate"
            value={endDate}
            onChange={(e) => handleEndDateChange(e.target.value)}
            className="input-style"
            disabled={!startDate}
            min={startDate}
          />
        </div>
        <div style={{ alignSelf: 'flex-end' }}>
          <button onClick={fetchSummaryCompanies} className="button-style" disabled={isLoading || !startDate || !endDate}>
            {isLoading ? 'Buscando...' : 'Buscar'}
          </button>
        </div>
      </div>

      <fieldset className="dashboard-fieldset">
        <legend className="dashboard-legend">Resumos Gerais</legend>
        <div className="cards-container">
          <div className="card card-small bg-primary">
            <h4 className="graphic-card">Total de Clientes</h4>
            <p className="graphic-card">{summary ? summary.total_clientes : '-'}</p>
          </div>
          <div className="card card-small bg-secondary">
            <h4 className="graphic-card">Total de Visitas</h4>
            <p className="graphic-card">{summary ? summary.total_visitas : '-'}</p>
          </div>
          <div className="card card-small bg-success">
            <h4 className="graphic-card">Pontos Ganhos</h4>
            <p className="graphic-card">{summary ? summary.saldo_pontos : '-'}</p>
          </div>
          <div className="card card-small bg-danger">
            <h4 className="graphic-card">Pontos Resgatados</h4>
            <p className="graphic-card">{summary ? summary.pontos_resgatados : '-'}</p>
          </div>
          <div className="card card-small bg-warning">
            <h4 className="graphic-card">Total Gasto</h4>
            <p className="graphic-card">
              {summary ? `R$ ${parseFloat(summary.total_gasto || 0).toFixed(2)}` : '-'}
            </p>
          </div>
        </div>
      </fieldset>

      <fieldset className="dashboard-fieldset">
        <legend className="dashboard-legend">Ticket Medio</legend>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '20px', justifyContent: 'center' }}>
          <div className="chart-container" style={{ height: '300px' }}>
            <h3>Ticket Medio por Dia</h3>
            {ticketMedio.length > 0 ? (
              <Line options={lineChartOptions} data={ticketMedioChartData} />
            ) : (
              <p>{hasSummarySearched ? 'Sem Informações no momento' : 'Selecione um lojista e clique em Buscar para ver os dados.'}</p>
            )}
          </div>
        </div>
      </fieldset>

      <fieldset className="dashboard-fieldset">
        <legend className="dashboard-legend">Participação de Emails</legend>
        {emailParticipation.length > 0 ? (
          <table className="dashboard-table">
            <thead>
              <tr>
                <th>
                  <button type="button" style={sortableHeaderStyle} onClick={() => handleEmailParticipationSort('nomeFantasia')}>
                    {getSortLabel('Nome Fantasia', 'nomeFantasia')}
                  </button>
                </th>
                <th>
                  <button type="button" style={sortableHeaderStyle} onClick={() => handleEmailParticipationSort('total_emails_enviados')}>
                    {getSortLabel('Total de Emails Enviados', 'total_emails_enviados')}
                  </button>
                </th>
                <th>
                  <button type="button" style={sortableHeaderStyle} onClick={() => handleEmailParticipationSort('participacao')}>
                    {getSortLabel('Participacao', 'participacao')}
                  </button>
                </th>
              </tr>
            </thead>
            <tbody>
              {sortedEmailParticipation.map((item, index) => (
                <tr key={`${item.nomeFantasia}-${index}`}>
                  <td>{item.nomeFantasia}</td>
                  <td>{item.total_emails_enviados}</td>
                  <td>{`${parseFloat(item.participacao || 0).toFixed(2)}%`}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <p>{hasEmailParticipationSearched ? 'Sem Informações no período' : 'Buscando informacoes do mes vigente.'}</p>
        )}
      </fieldset>
    </section>
  );
}

export default SummaryCompanies;
